import request from '@/src/apis/axios';
import { ListResponse, ObjectResponse } from '@/src/types/common';
import { plainToInstance } from 'class-transformer';
import {
  PutChangePasswordRequestBody,
  PostSignInRequestBody,
  GetAdministratorsRequestParams,
  UpsertAdministratorRequestBody,
} from '@/src/apis/rest/administrators.dtos';
import { AdministratorDto } from '@/src/models/AdministratorDto';

const URL = {
  signIn: '/admin/administrators/sessions/sign-in',
  signOut: '/admin/administrators/sessions/sign-out',
  administrators: '/admin/administrators',
  administratorsEdit: '/admin/administrators/{id}',
  administratorsDetail: '/admin/administrators/{id}',
  passwordChange: '/admin/administrators/{id}/password',
  passwordReset: '/admin/administrators/{id}/reset-password',
};

export const postSignInAPI = async (requestBody: PostSignInRequestBody) => {
  const { data, headers } = await request<ObjectResponse<AdministratorDto>>({
    method: 'post',
    url: URL.signIn,
    requestBody,
  });

  return {
    auth: plainToInstance(AdministratorDto, data.row),
    token: headers['authorization'],
  };
};

export const postSignOutAPI = async () => {
  const { data } = await request<ObjectResponse<AdministratorDto>>({
    method: 'post',
    url: URL.signOut,
  });

  return data.row;
};

export const putChangePasswordAPI = async (
  id: number,
  requestBody: PutChangePasswordRequestBody,
) => {
  const { status } = await request({
    method: 'put',
    url: URL.passwordChange.replace('{id}', String(id)),
    requestBody,
  });

  return status;
};

export const postAdministratorsAPI = async (
  requestBody: UpsertAdministratorRequestBody,
) => {
  const { data } = await request<ObjectResponse<AdministratorDto>>({
    method: 'post',
    url: URL.administrators,
    requestBody,
  });

  return plainToInstance(AdministratorDto, data.row);
};

export const putAdministratorsAPI = async (
  id: number,
  requestBody: UpsertAdministratorRequestBody,
) => {
  const { data } = await request<ObjectResponse<AdministratorDto>>({
    method: 'put',
    url: URL.administratorsEdit.replace('{id}', String(id)),
    requestBody,
  });

  return plainToInstance(AdministratorDto, data.row);
};

export const getAdministratorAPI = async (
  requestParams: Partial<GetAdministratorsRequestParams>,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;

  const { data } = await request<ListResponse<AdministratorDto>>({
    method: 'get',
    url: URL.administrators,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(AdministratorDto, data.rows);

  return { rows, count: data.count };
};

export const getAdministratorByIdAPI = async (id: number) => {
  const { data } = await request<ObjectResponse<AdministratorDto>>({
    method: 'get',
    url: URL.administratorsDetail.replace('{id}', String(id)),
  });

  return plainToInstance(AdministratorDto, data.row);
};

export const putPasswordResetAPI = async (id: number) => {
  const { status } = await request({
    method: 'put',
    url: URL.passwordReset.replace('{id}', String(id)),
  });

  return status;
};
